import { useMutation, useQuery } from "@apollo/client/react";
import { Skeleton, Stack, Tabs, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconCoin,
  IconHistory,
  IconInfoCircle,
  IconKey,
} from "@tabler/icons-react";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import { CreationForm } from "../../components/form/CreationForm";
import { AppHeader } from "./app-detail/AppHeader";
import { CredentialsTab } from "./app-detail/CredentialsTab";
import { MonetizationTab } from "./app-detail/MonetizationTab";
import { OverviewTab } from "./app-detail/OverviewTab";
import { ReleasesTab } from "./app-detail/ReleasesTab";
import { StatsCards } from "./app-detail/StatsCards";
import {
  APP_DETAIL_QUERY,
  MARKETPLACE_RELEASES_QUERY,
  ORG_APP_QUERY,
  SET_PRICING_MUTATION,
  UPDATE_APP_MUTATION,
} from "./app-detail/queries";
import type { App, MarketplaceRelease, OrgApp } from "./app-detail/types";

export function AppDetail() {
  const { appId } = useParams<{ appId: string }>();

  // Marketplace app data (listing, stats, pricing, reviews)
  const { data, loading, refetch } = useQuery<any>(APP_DETAIL_QUERY, {
    variables: { id: appId },
    skip: !appId,
  });
  const [updateApp] = useMutation<any>(UPDATE_APP_MUTATION);
  const [setPricing] = useMutation<any>(SET_PRICING_MUTATION);

  const app: App | undefined = data?.marketplaceApp;

  // Organization app data (OAuth2 config, webhooks, releases)
  const { data: orgData, loading: orgLoading } = useQuery<any>(ORG_APP_QUERY, {
    variables: { id: app?.organizationAppId },
    skip: !app?.organizationAppId,
  });
  const orgApp: OrgApp | undefined = orgData?.organizationApp;

  // Marketplace releases (with bundle keys)
  const { data: releasesData, loading: releasesLoading } = useQuery<any>(
    MARKETPLACE_RELEASES_QUERY,
    {
      variables: { marketplaceAppId: appId },
      skip: !appId,
    },
  );
  const releases: MarketplaceRelease[] = releasesData?.appReleases ?? [];

  if (loading) {
    return (
      <Stack gap="lg">
        <Skeleton height={60} />
        <Skeleton height={80} />
        <Skeleton height={400} />
      </Stack>
    );
  }

  if (!app || !appId) {
    return <Text c="red">App not found</Text>;
  }

  const openEdit = () => {
    const modalId = `edit-app-listing-${appId}`;
    modals.open({
      modalId,
      title: "Update App Listing",
      size: "lg",
      children: (
        <CreationForm
          schemaDefinition={{
            name: {
              type: "INPUT",
              label: "App Name",
              yupConfig: yup.string().required().min(1).max(100),
              inputType: "text",
              defaultValue: app.name,
            },
            shortDescription: {
              type: "TEXTAREA",
              label: "Short Description",
              yupConfig: yup.string().required().min(1).max(256),
              defaultValue: app.shortDescription,
            },
            description: {
              type: "RICH_TEXT_EDITOR",
              label: "Description",
              yupConfig: yup.string().required().min(1).max(2000),
              defaultValue: app.description,
            },
            icon: {
              type: "INPUT",
              label: "Icon URL",
              yupConfig: yup.string().url().nullable(),
              inputType: "url",
              defaultValue: app.icon,
            },
            categories: {
              type: "MULTI_SELECT",
              label: "Categories",
              yupConfig: yup.array().of(yup.string()),
              defaultValue: app.categories,
              options: [
                { label: "Tournaments", value: "tournaments" },
                { label: "Events", value: "events" },
                { label: "Analytics", value: "analytics" },
                { label: "Moderation", value: "moderation" },
                { label: "Social", value: "social" },
                { label: "Commerce", value: "commerce" },
                { label: "Utility", value: "utility" },
                { label: "Content", value: "content" },
              ],
            },
            privacyPolicyUrl: {
              type: "INPUT",
              label: "Privacy Policy URL",
              yupConfig: yup.string().url().nullable(),
              inputType: "url",
              defaultValue: app.privacyPolicyUrl,
            },
            supportUrl: {
              type: "INPUT",
              label: "Support URL",
              yupConfig: yup.string().url().nullable(),
              inputType: "url",
              defaultValue: app.supportUrl,
            },
            websiteUrl: {
              type: "INPUT",
              label: "Website URL",
              yupConfig: yup.string().url().nullable(),
              inputType: "url",
              defaultValue: app.websiteUrl,
            },
          }}
          onSubmit={async (values) => {
            try {
              await updateApp({
                variables: {
                  id: appId,
                  input: values,
                },
              });

              notifications.show({
                title: "Updated",
                message: "App listing updated successfully.",
                color: "green",
                autoClose: 3000,
              });

              modals.close(modalId);
              refetch();
            } catch (error) {
              notifications.show({
                title: "Error",
                message:
                  error instanceof Error
                    ? error.message
                    : "Failed to update app listing",
                color: "red",
              });
            }
          }}
        />
      ),
    });
  };

  return (
    <Stack gap="lg">
      <AppHeader app={app} appId={appId} onEditListing={openEdit} />

      <StatsCards stats={app.stats} pricing={app.pricing} />

      <Tabs defaultValue="overview">
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={14} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="releases" leftSection={<IconHistory size={14} />}>
            Releases ({releases.length})
          </Tabs.Tab>
          <Tabs.Tab value="credentials" leftSection={<IconKey size={14} />}>
            Credentials
          </Tabs.Tab>
          <Tabs.Tab value="monetization" leftSection={<IconCoin size={14} />}>
            Monetization
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <OverviewTab app={app} />
        </Tabs.Panel>

        <Tabs.Panel value="releases" pt="md">
          {releasesLoading ? (
            <Stack gap="md">
              <Skeleton height={100} />
              <Skeleton height={100} />
            </Stack>
          ) : (
            <ReleasesTab releases={releases} appId={appId} />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="credentials" pt="md">
          {orgLoading ? (
            <Stack gap="md">
              <Skeleton height={60} />
              <Skeleton height={60} />
              <Skeleton height={60} />
            </Stack>
          ) : orgApp ? (
            <CredentialsTab orgApp={orgApp} webhooks={orgApp.appWebhooks} />
          ) : (
            <Text c="dimmed">Organization app data not available.</Text>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="monetization" pt="md">
          <MonetizationTab
            appId={appId}
            pricing={app.pricing}
            setPricing={setPricing}
            refetch={refetch}
            reviews={app.reviews.nodes}
            totalReviewCount={app.reviews.totalCount}
            averageRating={app.stats.averageRating}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
